import {
  config,
  PKCE_STATE_KEY,
  PKCE_VERIFIER_KEY,
  POST_LOGIN_REDIRECT_KEY,
} from "./config";
import { codeChallenge, generateCodeVerifier, generateState } from "./pkce";

// 清理超过 30 分钟的滞留 PKCE 条目(用户中途放弃的登录)。
function prunePkceEntries(): void {
  const cutoff = Date.now() - 30 * 60_000;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(`${PKCE_STATE_KEY}:`)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(k) ?? "{}") as { ts?: number };
      if (!v.ts || v.ts < cutoff) localStorage.removeItem(k);
    } catch {
      localStorage.removeItem(k);
    }
  }
}

// Begin the Authorization Code + PKCE flow. The browser is redirected to
// Nova OS's /oauth/authorize; on consent, Nova OS bounces back to
// /oauth/callback with ?code=...&state=...
//
// If WebCrypto Subtle is unavailable (SES / lockdown extensions / kiosk
// profiles strip `crypto.subtle`), the PKCE code-challenge can't be
// computed and the SPA would crash with an uncaught TypeError. Detect
// this case at the top of the flow and redirect to the /login email +
// password fallback page instead; preserve the original target via
// ?from= so the user lands back where they started after authenticating.
export async function beginLogin(currentPath: string): Promise<void> {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    const fallbackUrl = `/login?from=${encodeURIComponent(currentPath)}`;
    window.location.assign(fallbackUrl);
    return;
  }

  const verifier = generateCodeVerifier();
  const challenge = await codeChallenge(verifier);
  const state = generateState();

  // 按 state 值键控存进 localStorage(Jarod 2026-08-12 "state mismatch"):
  // sessionStorage 是单条覆盖式——第二个标签页发起登录会覆盖第一个的
  // state,先完成回调的那个必然 mismatch;新标签页里打开回调则整个丢失。
  // 键控条目互不覆盖,回调按返回的 state 取,CSRF 校验语义不变(没有
  // 对应条目=拒绝)。旧的 sessionStorage 双键仍写,兼容回滚。
  prunePkceEntries();
  localStorage.setItem(
    `${PKCE_STATE_KEY}:${state}`,
    JSON.stringify({ verifier, redirect: currentPath, ts: Date.now() }),
  );
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.oidcClientId,
    redirect_uri: `${window.location.origin}/oauth/callback`,
    scope: config.oidcScopes,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${config.novaOsBaseUrl}/oauth/authorize?${params.toString()}`);
}

// Called from /oauth/callback. Validates state, exchanges the code for a
// JWT, returns the token (+ rotating refresh token when offline_access was
// granted). Caller decides where to redirect next.
export async function completeLogin(searchParams: URLSearchParams): Promise<{
  token: string;
  refreshToken: string | null;
  redirectTo: string;
}> {
  const code = searchParams.get("code");
  const returnedState = searchParams.get("state");
  if (!code) throw new Error("OAuth callback missing 'code' parameter");

  // 优先按返回的 state 查键控条目(多标签页安全);找不到再回落旧的
  // sessionStorage 双键(兼容升级瞬间发起的旧流程)。
  let verifier: string | null = null;
  let storedRedirect: string | null = null;
  const keyed = returnedState
    ? localStorage.getItem(`${PKCE_STATE_KEY}:${returnedState}`)
    : null;
  if (keyed) {
    try {
      const parsed = JSON.parse(keyed) as { verifier: string; redirect?: string };
      verifier = parsed.verifier;
      storedRedirect = parsed.redirect ?? null;
    } catch {
      /* fall through to legacy path */
    }
    localStorage.removeItem(`${PKCE_STATE_KEY}:${returnedState}`);
  }
  if (!verifier) {
    const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
    if (!expectedState || expectedState !== returnedState) {
      throw new Error("OAuth state mismatch — possible CSRF; aborting login");
    }
    verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!verifier) throw new Error("OAuth verifier missing from sessionStorage");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: `${window.location.origin}/oauth/callback`,
    client_id: config.oidcClientId,
    code_verifier: verifier,
  });

  const r = await fetch(`${config.novaOsBaseUrl}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!r.ok) {
    const errBody = await r.text();
    throw new Error(`Token exchange failed: ${r.status} ${errBody}`);
  }
  const tokenResp = (await r.json()) as {
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
  };
  // Nova OS issues a JWT as access_token; id_token is also returned but the
  // dashboard convention is to use access_token for API auth.
  const token = tokenResp.access_token ?? tokenResp.id_token;
  if (!token) throw new Error("Token response missing access_token/id_token");

  const redirectTo = storedRedirect ?? sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? "/";
  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

  return { token, refreshToken: tokenResp.refresh_token ?? null, redirectTo };
}

// Exchange a refresh token for a fresh access token. Nova OS rotates the
// refresh token on every use (the old one is invalidated server-side), so
// the caller must persist BOTH returned values. Returns null on any
// failure — an expired/revoked/rotated-away refresh token is not an
// exceptional condition, it just means "fall back to interactive login".
export async function refreshAccessToken(refreshToken: string): Promise<{
  token: string;
  refreshToken: string;
} | null> {
  try {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: config.oidcClientId,
    });
    const r = await fetch(`${config.novaOsBaseUrl}/oauth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { access_token?: string; refresh_token?: string };
    if (!j.access_token || !j.refresh_token) return null;
    return { token: j.access_token, refreshToken: j.refresh_token };
  } catch {
    return null;
  }
}

// End-session: hand off to Nova OS's end-session endpoint with a
// post_logout_redirect_uri pointing at our root. Nova OS clears its
// nova_oidc_session cookie, then redirects the browser back; the
// protected-route guard kicks the user into a fresh login flow with
// no auto-re-auth (the cookie is gone).
//
// Token clearing happens at the caller (useAuth.signOut) for testability.
export function endSession(): void {
  const returnTo = encodeURIComponent(`${window.location.origin}/`);
  window.location.assign(
    `${config.novaOsBaseUrl}/oauth/end-session?post_logout_redirect_uri=${returnTo}`,
  );
}
